



































































































































import { Component, Vue } from "vue-property-decorator";
import { validationMixin } from "vuelidate";
import { required, minLength, sameAs } from "vuelidate/lib/validators";
import { AxiosError } from "axios";
import { auth } from "@/config/host.config";
import { AccessToken } from "@/services/accessDecoder";
import { getBirthday } from "@/services/dateTime/date-time-fofmat";
import {
  prepareCredentialsFields,
  fetchChallenge,
} from "@/api/AuthAPI/rsa-crypto";

import StandartModal from "@/components/app/standart-modal.vue";

interface IAccountInfo {
  login: string;
  rules: string;
  room_number: string;
  birthday: string;
  ip: Array<string>;
}

const CHANGE_PASSWORD_MAX_ATTEMPTS = 2;

function isRetryableCryptoError(error: AxiosError): boolean {
  const body = error.response?.data as { error?: string } | undefined;
  const code = body?.error;
  return (
    code === "nonce_invalid_or_used" ||
    code === "nonce_mismatch" ||
    code === "credentials_decrypt_error"
  );
}

@Component({
  mixins: [validationMixin],
  validations: {
    oldPassword: { required },
    newPassword: { required, minLength: minLength(6) },
    repeatPassword: { sameAsPassword: sameAs("newPassword") },
  },
  components: {
    StandartModal,
  },
})
export default class AccountModal extends Vue {
  user: IAccountInfo | null = null;
  loading = false;
  sending = false;

  oldPassword = "";
  newPassword = "";
  repeatPassword = "";

  login = sessionStorage.getItem("login") || "";

  get userRules(): string | null {
    return this.$store.getters.getUserRole;
  }

  get userId(): string {
    return AccessToken.id;
  }

  get userIps(): Array<string> {
    return this.user && Array.isArray(this.user.ip) ? this.user.ip : [];
  }

  get currentLogin(): string {
    return (this.user && this.user.login) || this.login;
  }

  getBirthday = getBirthday;

  async loadUser(): Promise<void> {
    this.loading = true;
    try {
      const res = await this.$axios.get(`${auth.getUser}/${this.userId}`);
      const data = Array.isArray(res.data) ? res.data[0] : res.data;
      this.user = data || null;
    } catch (error) {
      console.log(error);
    } finally {
      this.loading = false;
    }
  }

  resetForm(): void {
    this.user = null;
    this.oldPassword = "";
    this.newPassword = "";
    this.repeatPassword = "";
    this.$nextTick(() => this.$v.$reset());
  }

  notify(text: string, type = "success"): void {
    this.$notify({ group: "global", text, type });
  }

  async changePassword(): Promise<void> {
    for (let attempt = 0; attempt < CHANGE_PASSWORD_MAX_ATTEMPTS; attempt++) {
      const nonce = await fetchChallenge();
      const credentials = await prepareCredentialsFields(
        {
          login: this.currentLogin,
          password: this.newPassword,
          old_password: this.oldPassword,
        },
        nonce
      );

      const body: { [key: string]: unknown } = { ...credentials };
      if (nonce) body.nonce = nonce;

      try {
        const res = await this.$axios.put(`${auth.user}/${this.userId}`, body);
        if (res.status === 200) return;
      } catch (e) {
        const error = e as AxiosError;
        if (
          attempt < CHANGE_PASSWORD_MAX_ATTEMPTS - 1 &&
          isRetryableCryptoError(error)
        ) {
          continue;
        }
        throw error;
      }
    }
    throw new Error("change password failed after retries");
  }

  async submit(): Promise<void> {
    this.$v.$touch();
    if (this.$v.$invalid) return;

    this.sending = true;
    try {
      await this.changePassword();
      this.notify(String(this.$i18n.t("Account.passwordChanged")));
      this.$bvModal.hide("account-modal");
    } catch (error) {
      const status = (error as AxiosError).response?.status;
      if (status === 403) {
        this.notify(String(this.$i18n.t("Account.wrongOldPassword")), "warn");
      } else {
        this.notify(String(this.$i18n.t("error.500")), "warn");
      }
    } finally {
      this.sending = false;
    }
  }
}
